import React, { useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import NdaStatusBadge from './NdaStatusBadge';
import { NDA_STATUSES, NdaStatus } from 'models/Nda';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import { getUserFullName, isNdaNotSent } from 'utils';
import LinkToIcon from 'icons/LinkTo.icon';
import useNdaStatus, { UpdateLeadNdaProps } from 'hooks/useNdaStatus';
import AutomaticMessageModal from 'components/AutomaticMessageModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import TransWithNda from 'components/TransWithNda';
import { SHORT_MAX_CHARS } from 'components/TransWithNda/TransWithNda.component';
import { getHasNdaToSend, getIsNativeNda } from 'agreementUtils';

import './NdaSigningProgress.styles.scss';
import './NdaSigningProgress.styles.responsive.scss';

type NdaSigningProgressProps = {
  className?: string;
};

const NdaSigningProgress: React.FC<NdaSigningProgressProps> = (props) => {
  const { className } = props;

  const { currentSession, updateCurrentSession } = useContext(MessagingContext);

  const {
    leadId,
    interlocutor,
    signingStatus,
    envelopeId,
    sentDate,
    signedDate,
    manuallySetNDAStatus,
    rejectionReason,
    envelopeTemplateId,
    envelopeTemplateName,
    ndaProvider,
    agreementExecutionId,
    agreementExecuted,
  } = currentSession || {};

  const hasNdaToSend =
    getHasNdaToSend(envelopeTemplateId) || getIsNativeNda({ ndaProvider });

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const interlocutorFullName = useMemo(
    () => getUserFullName(interlocutor?.firstName, interlocutor?.lastName),
    [interlocutor?.firstName, interlocutor?.lastName],
  );

  const { t } = useTranslation();

  const updateNdaProps = useCallback(
    (leadNdaProps: UpdateLeadNdaProps) => updateCurrentSession(leadNdaProps),
    [updateCurrentSession],
  );

  const { isLoading, getAndOpenNdaLink, sendNda, resendNda } = useNdaStatus(
    leadId,
    envelopeId,
    updateNdaProps,
    { ndaProvider, agreementExecutionId, agreementExecuted },
  );

  const classes = classNames(
    'nda-signing-progress',
    { 'is-loading': isLoading },
    className,
  );

  const CONTENT: Record<NdaStatus, JSX.Element> = useMemo(() => {
    const buttonProps: any = {
      icon: <LinkToIcon />,
      iconPosition: 'right',
      className: 'as-link',
      disabled: isLoading,
    };

    return {
      [NDA_STATUSES.NOT_SENT]: (
        <>
          <Button {...buttonProps} onClick={modal?.open}>
            <TransWithNda
              i18nKey="sendCATo"
              maxChars={SHORT_MAX_CHARS}
              values={{ recepient: interlocutorFullName }}
              envelopeTemplateName={envelopeTemplateName}
            />
          </Button>

          <AutomaticMessageModal
            ref={modalRef}
            leadFirstName={interlocutor?.firstName}
            leadLastName={interlocutor?.lastName}
            envelopeTemplateName={envelopeTemplateName}
            onSendNda={(message) => {
              modal?.close();
              sendNda(message);
            }}
          />
        </>
      ),
      [NDA_STATUSES.SENT]: (
        <>
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.SENT}
            sentDate={sentDate}
          />
          {hasNdaToSend && (
            <Button {...buttonProps} onClick={resendNda}>
              <TransWithNda
                i18nKey="resendNDA"
                maxChars={SHORT_MAX_CHARS}
                envelopeTemplateName={envelopeTemplateName}
              />
            </Button>
          )}
        </>
      ),
      [NDA_STATUSES.SIGNED]: (
        <>
          <NdaStatusBadge
            envelopeTemplateName={envelopeTemplateName}
            status={NDA_STATUSES.SIGNED}
            signedDate={signedDate}
          />
          {!manuallySetNDAStatus && (
            <div>
              <Button {...buttonProps} onClick={getAndOpenNdaLink}>
                <TransWithNda
                  i18nKey="previewSignedCA"
                  maxChars={SHORT_MAX_CHARS}
                  envelopeTemplateName={envelopeTemplateName}
                />
              </Button>
            </div>
          )}
        </>
      ),
      [NDA_STATUSES.REJECTED]: (
        <NdaStatusBadge
          envelopeTemplateName={envelopeTemplateName}
          status={NDA_STATUSES.REJECTED}
          rejectionReason={rejectionReason}
        />
      ),
      [NDA_STATUSES.COUNTER_SIGNATURE_PENDING]: (
        <NdaStatusBadge
          envelopeTemplateName={envelopeTemplateName}
          status={NDA_STATUSES.COUNTER_SIGNATURE_PENDING}
          signedDate={signedDate}
        />
      ),
    };
  }, [
    isLoading,
    modal,
    interlocutorFullName,
    envelopeTemplateName,
    modalRef,
    interlocutor?.firstName,
    interlocutor?.lastName,
    sentDate,
    hasNdaToSend,
    resendNda,
    signedDate,
    manuallySetNDAStatus,
    getAndOpenNdaLink,
    rejectionReason,
    sendNda,
  ]);

  if (!isNdaNotSent(signingStatus))
    return (
      <div className={classes}>
        <p className="title">{t('nonDisclosureAgreement')}</p>
        {CONTENT[signingStatus]}
      </div>
    );

  if (!hasNdaToSend)
    return (
      <div className="nda-signing-progress--empty">
        <p className="title">{t('nonDisclosureAgreement')}</p>
        <p>{t('youDontHaveNda')}</p>
      </div>
    );

  return (
    <div className={classes}>
      <p className="title">{t('nonDisclosureAgreement')}</p>
      {CONTENT[signingStatus]}
    </div>
  );
};

export default NdaSigningProgress;
